import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import DialogueBase from '../../../components/dialogues/DialogueBase';
import Button from '../../../components/Button';
import FileDropZone from './FileDropZone';
import FileUploadItem from './FileUploadItem';
import Typography from '@material-ui/core/Typography';
import CloudDoneIcon from '@material-ui/icons/CloudDone';
import * as track from '../../../lib/track';

const useStyles = makeStyles(theme => ({
  root: {
    maxWidth: '50rem',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    alignItems: 'center',
    marginBottom: '2rem',
  },
  buttons: {
    display: 'flex',
    flexWrap: 'nowrap',
    justifyContent: 'flex-start',
    alignItems: 'flex-end',
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      flexWrap: 'wrap',
      justifyContent: 'center',
      width: '100%',
      padding: '1rem 0',
    },
  },
  button: {
    marginRight: '1rem',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      marginBottom: '1rem',
    },
  },
  icon: {
    fontSize: '5rem',
    marginRight: '1.5rem',
    marginBottom: '1rem',
  },
}));

const FileUploadDialogue = ({open, onClose}) => {
  const classes = useStyles();
  const [files, setFiles] = useState([]);
  const [upload, setUpload] = useState(false);
  const [completed, setCompleted] = useState([]);
  const [done, setDone] = useState(false);

  const addFiles = filesToAdd => {
    const fileNamesToAdd = filesToAdd.map(f => f.name);
    const filteredFiles = files.filter(f => !fileNamesToAdd.includes(f.name));
    const fileSet = [...filteredFiles, ...filesToAdd];
    setFiles(fileSet);
  };

  const removeFile = fileName => {
    const fileSet = files.filter(f => f.name !== fileName);
    setFiles(fileSet);
  };

  const triggerUpload = () => {
    track.sendEvent('confirm upload button', 'click', {});
    if (files.length < 1) return;
    setUpload(true);
  };

  const onCompleted = fileName => {
    let filesCompleted = completed.map(f => f);
    filesCompleted.push(fileName);
    setCompleted(filesCompleted);
  };

  const clickCancel = () => {
    track.sendEvent('cancel upload button', 'click', {});
    onClose();
  };

  useEffect(() => {
    if (files.length > 0 && completed.length === files.length) {
      setUpload(false);
      setDone(true);
    }
  }, [completed]); // eslint-disable-line

  useEffect(() => {
    setCompleted([]);
    setFiles([]);
    setUpload(false);
    setDone(false);
  }, [open]); // eslint-disable-line

  return (
    <DialogueBase open={open} onClose={onClose} title="Upload documents">
      <div className={classes.root}>
        {done ? (
          <Fragment>
            <CloudDoneIcon color="secondary" className={classes.icon} />
            <Typography variant="h3">All files uploaded successfully!</Typography>
          </Fragment>
        ) : (
          <FileDropZone addFiles={addFiles} />
        )}
        {files.map((f, i) => (<FileUploadItem key={i} file={f} removeFile={removeFile} upload={upload} onCompleted={onCompleted}/>))}
      </div>
      <div className={classes.buttons}>
        {done ? (
          <Button
            variant="contained"
            label="Back to page"
            cssStyle="large"
            pointer="right"
            onClick={onClose}
          />
        ) : (
          <Fragment>
            <Button
              variant="outlined"
              label="Cancel"
              cssStyle="large"
              pointer="none"
              onClick={clickCancel}
              className={classes.button}
            />
            <Button
              variant="contained"
              label="Upload"
              cssStyle="large"
              pointer="none"
              onClick={triggerUpload}
              className={classes.button}
              disabled={files.length === 0}
            />
          </Fragment>
        )}
      </div>
    </DialogueBase>
  );
};

FileUploadDialogue.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default FileUploadDialogue;
